// Libs
import classNames from 'classnames/bind';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseTable } from '~/components/common';
// Others
import { LabelTypeDiscountEnum, TypeDiscountEnum } from '~/utils/enum/common';
import { IHeaderCellType } from '~/utils/interface/common';
import {
  formatCurrency,
  formatDate,
  formatDecimal,
  formatPhoneNumber,
  getFullAddress,
  getFullName,
} from '~/utils/helper/common';
import {
  DEFAULT_NUMBER_ZERO,
  EMAIL_SYSTEM,
  EMPTY_STRING,
  PERCENTAGE,
  PHONE_SYSTEM,
  POUND,
} from '~/utils/constants/common';
import {
  IDiscountInvoice,
  IInvoiceDetail,
  IMaterialInvoice,
  IServiceInvoice,
} from '~/utils/interface/invoice';
// Styles, images, icons
import styles from './DetailInvoice.module.scss';
import { icons, images } from '~/assets';

type Props = {
  data?: IInvoiceDetail;
};

const cx = classNames.bind(styles);

const columnsService = (t: TFunction): IHeaderCellType<IServiceInvoice>[] => {
  return [
    {
      id: 'name',
      label: t('tab_line_items_services_table_name'),
      render: (record: IServiceInvoice) => record?.name || EMPTY_STRING,
    },
    {
      id: 'quantity',
      label: t('tab_line_items_services_table_quantity'),
      render: (record: IServiceInvoice) =>
        record?.quantity ? formatDecimal(Number(record?.quantity)) : DEFAULT_NUMBER_ZERO,
      width: 150,
    },
    {
      id: 'price',
      label: t('tab_line_items_services_table_price'),
      render: (record: IServiceInvoice) =>
        record.price ? formatCurrency(Number(record.price)) : EMPTY_STRING,
      width: 150,
    },
    {
      id: 'amount',
      label: t('tab_line_items_services_table_amount'),
      render: (record: IServiceInvoice) =>
        record.totalPrice ? formatCurrency(Number(record.totalPrice)) : EMPTY_STRING,
      width: 150,
    },
  ];
};

const columnsMaterial = (t: TFunction): IHeaderCellType<IMaterialInvoice>[] => {
  return [
    {
      id: 'name',
      label: t('tab_line_items_materials_table_name'),
      render: (record: IMaterialInvoice) => record?.name || EMPTY_STRING,
    },
    {
      id: 'quantity',
      label: t('tab_line_items_materials_table_quantity'),
      render: (record: IMaterialInvoice) =>
        record?.quantity ? formatDecimal(Number(record?.quantity)) : DEFAULT_NUMBER_ZERO,
      width: 150,
    },
    {
      id: 'price',
      label: t('tab_line_items_materials_table_price'),
      render: (record: IMaterialInvoice) =>
        record.price ? formatCurrency(Number(record.price)) : EMPTY_STRING,
      width: 150,
    },
    {
      id: 'amount',
      label: t('tab_line_items_materials_table_amount'),
      render: (record: IMaterialInvoice) =>
        record.totalPrice ? formatCurrency(Number(record.totalPrice)) : EMPTY_STRING,
      width: 150,
    },
  ];
};

const columnsDiscount = (t: TFunction): IHeaderCellType<IDiscountInvoice>[] => {
  return [
    {
      id: 'description',
      label: t('invoice_detail_in_job_detail_discount_table_description'),
      render: (record) => record?.description || EMPTY_STRING,
      width: 150,
    },
    {
      id: 'type',
      label: t('invoice_detail_in_job_detail_discount_table_type'),
      render: (record) =>
        record?.type === TypeDiscountEnum.DOLLAR
          ? LabelTypeDiscountEnum.DOLLAR
          : LabelTypeDiscountEnum.PERCENTAGE,
      width: 150,
    },
    {
      id: 'rate',
      label: t('invoice_detail_in_job_detail_discount_table_rate'),
      render: (record) => (record.rate ? formatCurrency(Number(record.rate)) : EMPTY_STRING),
      width: 150,
    },
  ];
};

const DetailInvoice = (props: Props) => {
  //#region Destructuring Props
  const { data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box component={'div'} id='detailInvoice' className={cx('container')}>
      <Stack
        component={'div'}
        className={cx('headerContentWrap')}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={'12px'}
      >
        <Stack component={'div'} direction={'row'} alignItems={'center'} gap={'12px'}>
          <img src={images.imgLogo} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('logo')} />

          <Stack component={'div'} direction={'column'}>
            <Box component={'div'} className={cx('logoTitle')}>
              {t('invoice_detail_in_job_detail_noble_plumbing_title')}
            </Box>

            <Box component={'div'} className={cx('label')}>
              {t('invoice_detail_in_job_detail_noble_plumbing_address')}
            </Box>
          </Stack>
        </Stack>

        <Stack component={'div'} direction={'column'} spacing={'4px'}>
          <Stack component={'div'} direction={'row'} gap={'8px'}>
            <Typography component={'p'} className='text-24-500-normal'>
              {t('invoice_detail_in_job_detail_invoice_id_label')}
            </Typography>

            <Typography component={'p'} className='text-24-500-normal'>
              {data?.id ? `${POUND}${data?.id}` : EMPTY_STRING}
            </Typography>
          </Stack>

          <Stack component={'div'} className={cx('subValue')} direction={'row'} gap={'8px'}>
            <Typography className={cx('labelDate')} component={'p'}>
              {t('invoice_detail_in_job_detail_invoice_date_label')}
            </Typography>

            <Typography className={cx('value')} component={'p'}>
              {formatDate(new Date())}
            </Typography>
          </Stack>

          <Stack component={'div'} className={cx('subValue')} direction={'row'} gap={'8px'}>
            <Typography className={cx('labelDate')} component={'p'}>
              {t('invoice_detail_in_job_detail_due_date_label')}
            </Typography>

            <Typography className={cx('value')} component={'p'}>
              {data?.dueDate ? formatDate(data?.dueDate) : EMPTY_STRING}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack component={'div'} direction={'row'} gap={'24px'} className={cx('informationWrap')}>
        <Stack component={'div'} direction={'column'} gap={'8px'} width={'60%'}>
          <Typography component={'p'} className={cx('title', 'subTitle')}>
            {t('invoice_detail_in_job_detail_information_title')}
          </Typography>
          <Stack component={'div'} direction={'row'} alignItems={'center'} gap={'12px'}>
            <Typography className={cx('label')} component={'p'} minWidth={'100px'}>
              {t('invoice_detail_in_job_detail_customer_label')}
            </Typography>

            <Typography className={cx('value')} component={'p'}>
              {data?.customer ? getFullName(data?.customer) : EMPTY_STRING}
            </Typography>
          </Stack>

          <Stack component={'div'} direction={'row'} alignItems={'center'} gap={'12px'}>
            <Typography className={cx('label')} component={'p'} minWidth={'100px'}>
              {t('invoice_detail_in_job_detail_address_label')}
            </Typography>

            <Typography className={cx('value')} component={'p'}>
              {data?.customer?.customerAddresses?.[DEFAULT_NUMBER_ZERO]
                ? getFullAddress(data?.customer?.customerAddresses?.[DEFAULT_NUMBER_ZERO])
                : EMPTY_STRING}
            </Typography>
          </Stack>

          <Stack component={'div'} direction={'row'} alignItems={'center'} gap={'12px'}>
            <Typography className={cx('label')} component={'p'} minWidth={'100px'}>
              {t('invoice_detail_in_job_detail_email_label')}
            </Typography>

            <Typography className={cx('value')} component={'p'}>
              {data?.customer?.email || EMPTY_STRING}
            </Typography>
          </Stack>

          <Stack component={'div'} direction={'row'} alignItems={'center'} gap={'12px'}>
            <Typography className={cx('label')} component={'p'} minWidth={'100px'}>
              {t('invoice_detail_in_job_detail_amount_due_label')}
            </Typography>

            <Typography className={cx('value')} component={'p'}>
              {formatCurrency(data?.total || DEFAULT_NUMBER_ZERO)}
            </Typography>
          </Stack>
        </Stack>

        <Stack component={'div'} direction={'column'} gap={'8px'} width={'40%'}>
          <Typography component={'p'} className={cx('title', 'subTitle')}>
            {t('invoice_detail_in_job_detail_contact_us_title')}
          </Typography>

          <Stack component={'div'} direction={'row'} gap={'8px'}>
            <img
              src={icons.pipelineIconPhone}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('icon')}
            />

            <Typography className={cx('value')} component={'div'}>
              {formatPhoneNumber(PHONE_SYSTEM)}
            </Typography>
          </Stack>

          <Stack component={'div'} direction={'row'} gap={'8px'}>
            <img
              src={icons.pipelineIconEmail}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('icon')}
            />
            <Typography className={cx('value')} component={'p'}>
              {EMAIL_SYSTEM}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={'24px'} component={'div'} className={cx('lineItemsWrap')}>
        <Typography component={'p'} className={cx('title', 'subTitle')}>
          {t('invoice_detail_in_job_detail_line_items_title')}
        </Typography>

        {data?.services && data?.services?.length > DEFAULT_NUMBER_ZERO && (
          <Stack component={'div'} direction={'column'} gap={'12px'}>
            <Typography className={cx('titleTable')}>
              {t('invoice_detail_in_job_detail_service_table_title')}
            </Typography>

            <BaseTable columns={columnsService(t)} dataSource={data?.services || []} />
          </Stack>
        )}

        {data?.materials && data?.materials?.length > DEFAULT_NUMBER_ZERO && (
          <Stack component={'div'} direction={'column'} gap={'12px'}>
            <Typography className={cx('titleTable')}>
              {t('invoice_detail_in_job_detail_material_table_title')}
            </Typography>

            <BaseTable columns={columnsMaterial(t)} dataSource={data?.materials || []} />
          </Stack>
        )}

        {data?.discounts && data?.discounts?.length > DEFAULT_NUMBER_ZERO && (
          <Stack component={'div'} direction={'column'} gap={'12px'}>
            <Typography className={cx('titleTable')}>
              {t('invoice_detail_in_job_detail_discount_table_title')}
            </Typography>

            <BaseTable columns={columnsDiscount(t)} dataSource={data?.discounts || []} />
          </Stack>
        )}

        {!data?.services?.length && !data?.materials?.length && !data?.discounts?.length && (
          <Typography component={'p'} className={cx('noDataAvailable')}>
            {t('common_no_data', { ns: 'common/base' })}
          </Typography>
        )}
      </Stack>

      <Stack direction={'row'} justifyContent={'flex-end'} component={'div'}>
        <Stack direction={'column'} justifyContent={'flex-end'} component={'div'} spacing={'12px'}>
          <Stack direction={'row'} component={'div'} gap={1}>
            <Typography className={cx('totalLabel')}>
              {t('invoice_detail_in_job_detail_subtotal_label')}
            </Typography>
            <Typography className={cx('totalValue')}>
              {formatCurrency(data?.subTotal || DEFAULT_NUMBER_ZERO)}
            </Typography>
          </Stack>

          <Stack direction={'row'} component={'div'} gap={1}>
            <Typography className={cx('totalLabel')}>
              {t('invoice_detail_in_job_detail_tax_label')}
            </Typography>
            <Typography className={cx('valueDiscount')}>
              {DEFAULT_NUMBER_ZERO}
              {PERCENTAGE}
            </Typography>
          </Stack>

          <Stack direction={'row'} component={'div'} gap={1}>
            <Typography className={cx('totalLabel')}>
              {t('invoice_detail_in_job_detail_amount_due_label')}
            </Typography>
            <Typography className={cx('totalValue')}>
              {formatCurrency(data?.total || DEFAULT_NUMBER_ZERO)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Typography component={'p'} className={cx('value')}>
        {data?.message || EMPTY_STRING}
      </Typography>
    </Box>
  );
};

export default memo(DetailInvoice);
